import type React from 'react';
import { type ClassNames, type CustomComponents, DayPicker, type DayPickerProps } from 'react-day-picker';

import { cn } from '~/utils/cn.ts';

import { buttonVariants } from './button.tsx';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
	components,
	className,
	classNames,
	showOutsideDays = true,
	...props
}: {
	components: Partial<CustomComponents> | undefined;
	className?: string;
	classNames?: Partial<ClassNames>;
	showOutsideDays?: boolean;
} & DayPickerProps) {
	return (
		<DayPicker
			className={cn('p-3', className)}
			classNames={{
				// Lyra style: sharp corners, text-xs
				caption: 'flex justify-center pt-1 relative items-center',
				caption_label: 'text-xs font-medium text-foreground',
				cell: cn(
					'relative p-0 text-center text-xs',
					'[&:has([aria-selected])]:bg-accent',
					'[&:has([aria-selected])]:rounded-none',
					'focus-within:relative focus-within:z-20',
				),
				day: cn(buttonVariants({ variant: 'ghost' }), 'h-8 w-8 rounded-none p-0 font-normal aria-selected:opacity-100'),
				day_disabled: 'text-muted-foreground opacity-50',
				day_hidden: 'invisible',
				day_outside: 'text-muted-foreground opacity-50',
				day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
				day_selected: cn(
					'rounded-none bg-primary text-primary-foreground',
					'hover:bg-primary/80 hover:text-primary-foreground',
					'focus:bg-primary focus:text-primary-foreground',
				),
				day_today: 'underline',
				head_cell: 'text-muted-foreground rounded-none w-8 font-normal text-xs',
				head_row: 'flex',
				month: cn('space-y-4', classNames?.month),
				months: cn('flex flex-row space-y-4 sm:space-x-4 sm:space-y-0', classNames?.months),
				multiple_months: '',
				nav: 'space-x-1 flex items-center',
				nav_button: cn(
					buttonVariants({ variant: 'outline' }),
					'h-7 w-7 rounded-none bg-transparent p-0 opacity-50 hover:opacity-100',
				),
				nav_button_next: 'absolute right-1',
				nav_button_previous: 'absolute left-1',
				row: 'flex w-full mt-2',
				table: 'w-full border-collapse space-y-1',
				...classNames,
			}}
			components={components}
			showOutsideDays={showOutsideDays}
			{...props}
		/>
	);
}
Calendar.displayName = 'Calendar';

export { Calendar };
